"use client";

import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";

const SOUNDTRACK_SRC = "/music/landingpage.mp3";
const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart"] as const;
const FADE_IN_DURATION_MS = 4000;
const FADE_STEPS = 40;
const TARGET_VOLUME = 0.15;

export default function LandingPageSoundtrack() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const hasStartedRef = useRef(false);
  const fadeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const removeInteractionListenersRef = useRef<() => void>(() => {});
  const [isMuted, setIsMuted] = useState(false);

  const fadeIn = (audio: HTMLAudioElement) => {
    if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
    audio.volume = 0;
    let step = 0;
    fadeTimerRef.current = setInterval(() => {
      step++;
      audio.volume = Math.min(
        TARGET_VOLUME,
        (step / FADE_STEPS) * TARGET_VOLUME,
      );
      if (step >= FADE_STEPS) {
        if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    }, FADE_IN_DURATION_MS / FADE_STEPS);
  };

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.loop = true;

    let listenersRemoved = false;

    const removeInteractionListeners = () => {
      if (listenersRemoved) {
        return;
      }

      listenersRemoved = true;
      INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleFirstInteraction);
      });
    };

    removeInteractionListenersRef.current = removeInteractionListeners;

    const startPlayback = async () => {
      if (hasStartedRef.current || audio.muted) {
        return;
      }

      try {
        audio.volume = 0;
        await audio.play();
        fadeIn(audio);
        hasStartedRef.current = true;
        removeInteractionListeners();
      } catch {
        // Autoplay can be blocked until the user interacts with the page.
      }
    };

    function handleFirstInteraction() {
      void startPlayback();
    }

    audio.currentTime = 0;
    void startPlayback();

    INTERACTION_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleFirstInteraction, {
        passive: true,
      });
    });

    return () => {
      removeInteractionListeners();
      removeInteractionListenersRef.current = () => {};
      hasStartedRef.current = false;
      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.muted = isMuted;

    if (!isMuted) {
      if (!hasStartedRef.current) {
        audio.volume = 0;
        void audio
          .play()
          .then(() => {
            fadeIn(audio);
            hasStartedRef.current = true;
            removeInteractionListenersRef.current();
          })
          .catch(() => {
            // Playback can still fail until the browser allows it.
          });
      } else {
        fadeIn(audio);
      }
    } else {
      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
    }
  }, [isMuted]);

  const handleToggleMute = () => {
    setIsMuted((current) => !current);
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={SOUNDTRACK_SRC}
        preload="auto"
        playsInline
        loop
        muted={isMuted}
        aria-hidden="true"
        className="hidden"
      />

      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-pressed={isMuted}
        aria-label={isMuted ? "Unmute soundtrack" : "Mute soundtrack"}
        title={isMuted ? "Unmute soundtrack" : "Mute soundtrack"}
        onPointerDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onClick={handleToggleMute}
        className="fixed bottom-5 right-5 z-[80] h-11 rounded-full border-white/15 bg-black/60 px-4 text-white shadow-[0_16px_40px_rgba(0,0,0,0.45)] backdrop-blur-xl transition hover:bg-black/80 hover:text-white md:bottom-8 md:right-8"
      >
        {isMuted ? (
          <VolumeX className="size-4" />
        ) : (
          <Volume2 className="size-4" />
        )}
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">
          {isMuted ? "Sound Off" : "Sound On"}
        </span>
      </Button>
    </>
  );
}
